import { cn } from '@risksmart-app/atomic-ui';
import type { RiskAssessmentResultConfig } from '@risksmart-app/domain/src/types/risk-assessment-result-config';
import { Fragment } from 'react';

import { PreviewCell } from './PreviewCell';

type MatrixCell = RiskAssessmentResultConfig['matrix'][number];

type MatrixPreviewProps = {
  config: RiskAssessmentResultConfig;
  likelihoodAxisLabel: string;
  impactAxisLabel: string;
};

const EMPTY_CELL_COLOR = '#E0E0E0';

const buildMatrixGrid = (matrix: MatrixCell[]): Map<string, MatrixCell> => {
  const grid = new Map<string, MatrixCell>();
  for (const entry of matrix) {
    grid.set(`${entry.likelihood}-${entry.impact}`, entry);
  }

  return grid;
};

const MatrixPreview = ({
  config,
  likelihoodAxisLabel,
  impactAxisLabel,
}: MatrixPreviewProps) => {
  const { likelihood, impact, matrix } = config;

  const grid = buildMatrixGrid(matrix);
  const sortedColumns = [...impact.ratings].sort((a, b) => a.value - b.value);
  const sortedRows = [...likelihood.ratings].sort((a, b) => a.value - b.value);

  const columnCount = sortedColumns.length;

  return (
    <div
      className={cn('grid w-full gap-2')}
      style={{
        gridTemplateColumns: `repeat(${columnCount + 1}, minmax(max-content, 1fr))`,
      }}
    >
      <PreviewCell
        label={impactAxisLabel}
        value={likelihoodAxisLabel}
        valueClassName={'text-sm'}
        className={cn(
          'bg-neutral-muted-minimal border border-neutral-border text-neutral-muted-foreground'
        )}
      />
      {sortedColumns.map((col) => (
        <PreviewCell
          key={`col-${col.value}`}
          color={col.color}
          label={col.title}
        />
      ))}

      {sortedRows.map((row) => (
        <Fragment key={`row-${row.value}`}>
          <PreviewCell color={row.color} label={row.title} />
          {sortedColumns.map((col) => {
            const cell = grid.get(`${row.value}-${col.value}`);

            return (
              <PreviewCell
                key={`${row.value}-${col.value}`}
                color={cell?.color ?? EMPTY_CELL_COLOR}
                label={cell?.title ?? '-'}
                value={cell?.value}
              />
            );
          })}
        </Fragment>
      ))}
    </div>
  );
};

export { MatrixPreview };
